import {
  Box,
  Button,
  Center,
  Text as ChakraText,
  HStack,
  IconButton,
  Image,
  Separator,
  Spinner,
  VStack
} from "@chakra-ui/react"
import Alert from "@pathwright/ui/src/components/alert/Alert"
import BlankSlate from "@pathwright/ui/src/components/blank/BlankSlate"
import SubmitButton from "@pathwright/ui/src/components/button/SubmitButton"
import Card from "@pathwright/ui/src/components/card/Card"
import CardBlock from "@pathwright/ui/src/components/card/CardBlock"
import CardHeader from "@pathwright/ui/src/components/card/CardHeader"
import ErrorMessage from "@pathwright/ui/src/components/error/ErrorMessage"
import Text from "@pathwright/ui/src/components/ui/Text"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import Tooltip from "@pathwright/ux/src/components/tooltip/Tooltip"
import classNames from "classnames"
import { useEffect, useMemo, useState } from "react"
import { useHistory } from "react-router-dom"
import styled from "styled-components"
import {
  MentorGroupSubscriptionFragment,
  OrderByDir,
  OrderStripeCheckoutRedirects,
  OrderType,
  PositionedSchoolSubscriptionPlanFragmentDoc,
  PositioningContextTypeInput,
  SchoolSubscriptionDiscount,
  SchoolSubscriptionPlanFragment,
  StripeProductFragment,
  SubscriptionTrialType,
  useRedeemSchoolGiftSubscriptionMutation,
  useSchoolSubscriptionPlansQuery,
  useSubscriptionDiscountLazyQuery,
  useUserSchoolSubscriptionQuery
} from "@pathwright/graphql-client/src/generated"
import LazyAdaptedAuthContainer from "../auth/LazyAdaptedAuthContainer"
import { useCurrencyFormatter } from "../currency/CurrencyFormatterProvider"
import SimpleGroupSubscriptionMentorGroup from "../mentor-group/groups/SimpleGroupSubscriptionMentorGroup"
import { usePathwrightContext } from "../pathwright/PathwrightContext"
import {
  PositioningContextProvider,
  sortPositionedObjects,
  usePositioningContext
} from "../positioning/PositioningContext"
import PWAdminHint from "../pw-admin/PWAdminHint"
import StripeProductCard from "../stripe/StripeProductCard"
import useStripeProducts from "../stripe/graphql/useStripeProducts"
import {
  getIsSubscriptionActive,
  getIsSubscriptionDelinquent
} from "../subscription/utils"
import { useUserCommunityContext } from "../user/UserCommunityContext"
import ConditionalOrderDiscountForm from "./ConditionalOrderDiscountForm"
import OrderStripeCheckoutForm, {
  useStripeCheckoutRedirects
} from "./OrderStripeCheckoutForm"
import { getIntervalDisplayName } from "./utils/subscription"

type OrderSubscriptionCardProps = {
  className?: string
  card?: any
  planIds?: string[]
  productIds?: string[]
  visibilityCode?: string
  visibilityCodeFilter?: {
    eq?: string
  }
  inviteToken?: string
  userPropertyDefaults?: {
    [key: string]: any
  }
  reactivateMentorGroupId?: number
  showStripProductCardBackButton?: boolean
  redirectTo?: string
  stripeCheckoutRedirects?: OrderStripeCheckoutRedirects
}

export default function OrderSubscriptionCard({
  className,
  card,
  planIds,
  productIds,
  visibilityCode,
  visibilityCodeFilter,
  inviteToken,
  userPropertyDefaults,
  stripeCheckoutRedirects,
  redirectTo
}: OrderSubscriptionCardProps) {
  const { school } = usePathwrightContext()
  const { tc, tca } = useTranslate()
  const { activeCommunityGroupSubscription } = useUserCommunityContext()
  const userSubscriptionQuery = useUserSchoolSubscriptionQuery()
  const userSubscription =
    userSubscriptionQuery.data?.me?.school_subscription?.subscription
  const hasActiveSubscription =
    getIsSubscriptionActive(userSubscription) ||
    !!activeCommunityGroupSubscription

  return (
    <NarrowCard
      className={classNames("OrderSubscriptionCard", className)}
      card={card}
      navless
      noaction
      emphasis="secondary"
    >
      <CardHeader
        card={card}
        title={
          hasActiveSubscription ? tca("Your Subscription") : tc("Subscribe")
        }
        meta={tc(`To {{ schoolName }}`, { schoolName: school.name })}
      />
      <Box
        css={{
          "& .OrderSubscriptionFlow": {
            p: 4
          }
        }}
      >
        <OrderSubscriptionFlow
          planIds={planIds}
          productIds={productIds}
          visibilityCode={visibilityCode}
          visibilityCodeFilter={visibilityCodeFilter}
          inviteToken={inviteToken}
          userPropertyDefaults={userPropertyDefaults}
          showStripProductCardBackButton
          stripeCheckoutRedirects={stripeCheckoutRedirects}
          redirectTo={redirectTo}
        />
      </Box>
    </NarrowCard>
  )
}

OrderSubscriptionCard.displayName = "OrderSubscriptionCard"

function OrderSubscriptionFlowSteps({
  planIds,
  productIds,
  visibilityCode,
  visibilityCodeFilter,
  redirectTo,
  inviteToken,
  userPropertyDefaults,
  showStripProductCardBackButton,
  stripeCheckoutRedirects
}: OrderSubscriptionCardProps) {
  const { me } = usePathwrightContext()
  const { reactivateCommunityGroupWithInactiveSubscription } =
    useUserCommunityContext()

  const { tc, tca } = useTranslate()

  // Selection controls step of subscribe flow
  const [selectedSubscriptionPlan, setSelectedSubscriptionPlan] =
    useState<SchoolSubscriptionPlanFragment | null>(null)
  const [selectedStripeProduct, setSelectedStripeProduct] =
    useState<StripeProductFragment | null>(null)

  // Ignoring groups with a seat limit of 1 or less which are considered "individual" subscriptions
  // and do not benefit from reactivation, whereas reactivating a "group" subscription will automatically
  // retain the attached mentor group + memberships.
  function getReactivateMentorGroup(group?: MentorGroupSubscriptionFragment) {
    return (group?.seat_limit || 0) > 1 ? group : undefined
  }
  const [reactivateMentorGroup, setReactivateMentorGroup] = useState(
    getReactivateMentorGroup(reactivateCommunityGroupWithInactiveSubscription)
  )

  // The reactivateCommunityGroupWithInactiveSubscription may not be available on mount,
  // so let's set once it exists (if it does).
  useEffect(() => {
    if (reactivateCommunityGroupWithInactiveSubscription) {
      setReactivateMentorGroup(
        getReactivateMentorGroup(
          reactivateCommunityGroupWithInactiveSubscription
        )
      )
    }
  }, [reactivateCommunityGroupWithInactiveSubscription])

  const [stripeProducts, stripeProductsQuery] = useStripeProducts(
    {
      ids: productIds
        ? {
            in: productIds
          }
        : null
    },
    { skip: productIds?.length === 0 }
  )

  const positioningContext = {
    contextType: PositioningContextTypeInput.SubscribeFlow,
    // TODO: what context key should be used?
    contextKey: "subscribe:flow"
  }
  const schoolSubscriptionPlansQuery = useSchoolSubscriptionPlansQuery({
    variables: {
      ids: planIds
        ? {
            in: planIds
          }
        : null,
      visibility_code: visibilityCode || null,
      visibility_code_filter: visibilityCodeFilter || null,
      positioningContext,
      orderBy: [{ position: OrderByDir.Asc }, { id: OrderByDir.Asc }]
    },
    skip: planIds?.length === 0
  })
  const flattenedSchoolSubscriptionPlans =
    schoolSubscriptionPlansQuery.data?.school?.subscription_plans?.edges?.map(
      (edge) => edge!.node!
    ) || []

  const schoolSubscriptionPlans = sortPositionedObjects(
    flattenedSchoolSubscriptionPlans
  )

  // Auto-select the first plan if there is only one and no stripe products selected.
  // We don't set in state but rather return a memoized value to avoid unnecessary re-renders,
  // specifically avoiding rendering the list of subscription plans briefly.
  const defaultSelectedSubscriptionPlan = useMemo(() => {
    if (
      schoolSubscriptionPlans.length === 1 &&
      stripeProducts.length === 0 &&
      !selectedSubscriptionPlan
    ) {
      return schoolSubscriptionPlans[0]
    }
    return null
  }, [
    schoolSubscriptionPlans,
    stripeProducts,
    planIds,
    productIds,
    selectedSubscriptionPlan
  ])

  const error = stripeProductsQuery.error || schoolSubscriptionPlansQuery.error
  const loading =
    stripeProductsQuery.loading || schoolSubscriptionPlansQuery.loading

  if (error) {
    return (
      <BlankSlate
        icon="statuscaution"
        heading={tc("discussion.responses.unexpected_error")}
        body={<ErrorMessage error={error} />}
      />
    )
  }

  if (loading) {
    return (
      <Center minH="300px" flex={1}>
        <Spinner />
      </Center>
    )
  }

  const totalOptions = schoolSubscriptionPlans.length + stripeProducts.length

  // The subscription plan to display, whether manually selected or automatically selected.
  const subscriptionPlan =
    selectedSubscriptionPlan || defaultSelectedSubscriptionPlan

  if (subscriptionPlan) {
    if (!me) {
      return (
        <LazyAdaptedAuthContainer
          inviteToken={inviteToken}
          userPropertyDefaults={userPropertyDefaults}
        />
      )
    }
    return (
      <CardBlock>
        {totalOptions > 1 && (
          <Button
            colorPalette="brand"
            variant="plain"
            textDecoration="underline"
            p={0}
            onClick={() => setSelectedSubscriptionPlan(null)}
          >
            {tca("Change Plan")}
          </Button>
        )}
        <SubscriptionPlanOrderForm
          subscriptionPlan={subscriptionPlan}
          visibilityCode={visibilityCode}
          visibilityCodeFilter={visibilityCodeFilter}
          redirectTo={redirectTo}
          showPlanDetails={totalOptions <= 1}
        />
      </CardBlock>
    )
  }

  if (totalOptions <= 0) {
    return (
      <BlankSlate icon="recur" heading={tc("No subscription plans found")} />
    )
  }

  return (
    <VStack className="OrderSubscriptionFlow" w="full">
      {!!reactivateMentorGroup && (
        <HStack bg="white" borderRadius="10px" py={2} px={3} gap={4}>
          <IconButton
            aria-label="clear group"
            variant="subtle"
            rounded="full"
            size="xs"
            children={<Pathicon icon="x" size={16} />}
            onClick={() => setReactivateMentorGroup(undefined)}
          />
          <SimpleGroupSubscriptionMentorGroup
            mentorGroup={reactivateMentorGroup}
          />
        </HStack>
      )}
      <div className="SubscriptionOptions" style={{ width: "100%" }}>
        {stripeProducts
          .filter((stripeProduct) => {
            // If a stripe product has been selected, only show that one.
            if (selectedStripeProduct) {
              return selectedStripeProduct.id === stripeProduct.id
            }

            // Filter out all stripe price + products that don't match the ones
            // set on the reactivateMentorGroup.
            // Backend prohibits reactivating a mentor group with a different product.
            // Only doing this when reactivating a mentor group subscription.
            return (
              !reactivateMentorGroup ||
              reactivateMentorGroup?.group_subscription?.price?.product?.id ===
                stripeProduct.id
            )
          })
          .map((stripeProduct) => (
            <StripeProductCard
              key={stripeProduct.id}
              stripeProduct={stripeProduct}
              onSelectStripeProduct={setSelectedStripeProduct}
              inviteToken={inviteToken}
              reactivateMentorGroup={reactivateMentorGroup}
              showBackButton={showStripProductCardBackButton}
              stripeCheckoutRedirects={stripeCheckoutRedirects}
              redirectTo={redirectTo}
            />
          ))}
        {!selectedStripeProduct && !reactivateMentorGroup && (
          <PositioningContextProvider
            items={schoolSubscriptionPlans}
            itemKey="subscription:plan"
            itemFragmentDocument={PositionedSchoolSubscriptionPlanFragmentDoc}
            context={positioningContext}
          >
            <>
              {schoolSubscriptionPlans.map((plan, index) => (
                <PositionedSubscriptionPlanCard
                  key={plan.id}
                  plan={plan}
                  onSelectPlan={setSelectedSubscriptionPlan}
                  showPlanDetails
                  index={index}
                />
              ))}
            </>
          </PositioningContextProvider>
        )}
      </div>
    </VStack>
  )
}

export function OrderSubscriptionFlow({
  planIds,
  productIds,
  visibilityCode,
  visibilityCodeFilter,
  inviteToken,
  userPropertyDefaults,
  showStripProductCardBackButton = false,
  stripeCheckoutRedirects,
  redirectTo
}: OrderSubscriptionCardProps) {
  const { school } = usePathwrightContext()
  const {
    communityGroupMemberships,
    isCommunityGroupAdmin,
    activeCommunityGroupSubscription,
    communityGroupWithActiveSubscription,
    hasActiveMembership
  } = useUserCommunityContext()

  const { t, tc, tca, Trans } = useTranslate()

  const userSubscriptionQuery = useUserSchoolSubscriptionQuery()
  const userSubscription =
    userSubscriptionQuery.data?.me?.school_subscription?.subscription

  const planVisibilityCode =
    visibilityCode || (visibilityCodeFilter && visibilityCodeFilter.eq)

  const error = userSubscriptionQuery.error
  const loading = userSubscriptionQuery.loading

  if (error) {
    return (
      <BlankSlate
        icon="statuscaution"
        heading={tc("discussion.responses.unexpected_error")}
        body={<ErrorMessage error={error} />}
      />
    )
  }

  if (loading) {
    return (
      <Center minH="300px" flex={1}>
        <Spinner />
      </Center>
    )
  }

  if (
    getIsSubscriptionActive(userSubscription) &&
    !userSubscription?.trial_end_dtime
  ) {
    return getIsSubscriptionDelinquent(userSubscription) ? (
      <BlankSlate
        icon="recur"
        heading="Your subscription is expired."
        body="When attempting to renew your subscription the payment method failed. After multiple attempts, your subscription was cancelled. To restore your subscription access, please update your payment method:"
        primaryAction={{
          label: "Update Payment Method",
          to: "/manage/profile/billing/payment/"
        }}
      />
    ) : (
      <BlankSlate
        icon="recur"
        heading={`${tc(`You’ve already subscribed to {{ school }}`, {
          school: school.name
        })}!`}
        body={`${tc(`You may manage your subscription under your profile`)}.`}
      />
    )
  }

  if (communityGroupMemberships.length) {
    if (isCommunityGroupAdmin) {
      if (communityGroupWithActiveSubscription) {
        const group = communityGroupWithActiveSubscription
        if ((group.seat_limit || 0) > 1) {
          return (
            <StyledBlankSlate
              stack
              icon="recur"
              heading={
                <>
                  <Trans
                    i18nKey="Your group subscription to<br />{{ school }} is active"
                    tOptions={{
                      school: school.name
                    }}
                  />
                </>
              }
              primaryAction={{
                to: `/community/${group.id}/`,
                children: tc("Manage my group")
              }}
              secondaryAction={{
                to: "/library",
                children: t("View included t('path.label_plural')")
              }}
            />
          )
        } else {
          return (
            <StyledBlankSlate
              stack
              icon="recur"
              heading={tc(`Your subscription to {{ school }} is active`, {
                school: school.name
              })}
              primaryAction={{
                to: "/library",
                children: t("View included t('path.label_plural')")
              }}
            />
          )
        }
      }
    } else {
      if (communityGroupWithActiveSubscription) {
        const group = communityGroupWithActiveSubscription
        const subscription = activeCommunityGroupSubscription
        return (
          <StyledBlankSlate
            icon="recur"
            heading={tc(`You are subscribed through {{ group }}`, {
              group: group.name
            })}
            body={
              <>
                {tc(
                  `You have access to all courses included in {{ subscription }}`,
                  { subscription: subscription.price?.product?.name }
                )}
                .
                <br />
                <br />
                <Trans
                  i18nKey={
                    "If you'd like to change your subscription, please contact {{ owner }} at <a>{{ email }}</a>."
                  }
                  components={{
                    a: <a href={`mailto:${group.owner?.email}`} />
                  }}
                  tOptions={{
                    owner: group.owner?.full_name,
                    email: group.owner?.email
                  }}
                />
              </>
            }
          />
        )
      } else {
        // Only prevent subscribing if user has an active membership (not revoked).
        const activeMembership = communityGroupMemberships.find((membership) =>
          hasActiveMembership(membership.group)
        )
        if (activeMembership) {
          const group = activeMembership.group
          return (
            <>
              <StyledBlankSlate
                icon="recur"
                heading={tca(`Subscription Unavailable`)}
                body={
                  <>
                    {tc(
                      `The {{ group }} subscription you are a part of isn't available`,
                      { group: group.name }
                    )}
                    .
                    <br />
                    <br />
                    <Trans
                      i18nKey="To resolve this issue, please contact the {{ group }} subscription owner, {{ owner }} at <a>{{ email }}</a>."
                      tOptions={{
                        group: group.name,
                        owner: group.owner?.full_name,
                        email: group.owner?.email
                      }}
                      components={{
                        a: <a href={`mailto:${group.owner?.email}`} />
                      }}
                    />
                  </>
                }
              />
              <HStack px={4}>
                <Separator flex="1" />
                <ChakraText flex="0" as="span">
                  {t("Or")}
                </ChakraText>
                <Separator flex="1" />
              </HStack>
              <OrderSubscriptionFlowSteps
                planIds={planIds}
                productIds={productIds}
                visibilityCode={visibilityCode}
                visibilityCodeFilter={visibilityCodeFilter}
                redirectTo={redirectTo}
                inviteToken={inviteToken}
                userPropertyDefaults={userPropertyDefaults}
                showStripProductCardBackButton={showStripProductCardBackButton}
                stripeCheckoutRedirects={stripeCheckoutRedirects}
              />
            </>
          )
        }
      }
    }
  }

  return (
    <OrderSubscriptionFlowSteps
      planIds={planIds}
      productIds={productIds}
      visibilityCode={visibilityCode}
      visibilityCodeFilter={visibilityCodeFilter}
      redirectTo={redirectTo}
      inviteToken={inviteToken}
      userPropertyDefaults={userPropertyDefaults}
      showStripProductCardBackButton={showStripProductCardBackButton}
      stripeCheckoutRedirects={stripeCheckoutRedirects}
    />
  )
}

type SubscriptionPlanOrderFormProps = {
  subscriptionPlan: SchoolSubscriptionPlanFragment
  visibilityCode?: string
  visibilityCodeFilter?: {
    eq?: string
  }
  stripeCheckoutRedirects?: OrderStripeCheckoutRedirects
  redirectTo?: string
  showPlanDetails?: boolean
}

function SubscriptionPlanOrderForm({
  subscriptionPlan,
  visibilityCode,
  visibilityCodeFilter,
  stripeCheckoutRedirects: stripeCheckoutRedirectsProp,
  redirectTo,
  showPlanDetails = false
}: SubscriptionPlanOrderFormProps) {
  const [loadDiscount] = useSubscriptionDiscountLazyQuery()
  const [discountCode, setDiscountCode] = useState("")
  const [discount, setDiscount] = useState<SchoolSubscriptionDiscount | null>(
    null
  )
  const history = useHistory()
  const localStripeCheckoutRedirects = useStripeCheckoutRedirects()
  const stripeCheckoutRedirects =
    stripeCheckoutRedirectsProp ?? localStripeCheckoutRedirects

  const { tc } = useTranslate()

  const userSubscriptionQuery = useUserSchoolSubscriptionQuery()

  // When a 100% discount coupon is applied, AND the subscription plan is not of the WITHOUT_CARD_STRIPE trial type,
  // we must bypass the order API and use the redeem API. For WITHOUT_CARD_STRIPE trial type, we MUST send the user through Stripe checkout
  // so that the subscription is created and managed in Stripe. This ensures that future subscription management actions
  // (such as upgrades, cancellations, and payment method changes) can be handled via Stripe's dashboard and APIs, rather than only in our database.
  const stripeCheckoutRequiredTrialTypes = [
    SubscriptionTrialType.WithCard,
    SubscriptionTrialType.WithoutCardStripe
  ]

  // The trial type of the subscription plan being ordered.
  const subscriptionTrialType = subscriptionPlan?.subscription_plan.trial_type

  // Determine if the discount makes the subscription fully discounted, requiring no payment.
  const isFullyDiscounted = (discount?.discount_percent || 0) >= 1

  const shouldRedeemAsGiftSubscription =
    isFullyDiscounted &&
    !stripeCheckoutRequiredTrialTypes.includes(subscriptionTrialType)

  const [redeemSchoolGiftSubscription, { error }] =
    useRedeemSchoolGiftSubscriptionMutation({
      variables: {
        school_subscription_plan_id: subscriptionPlan?.id!,
        code: discountCode
      },
      onCompleted: () => {
        // once subscription has been redeemed, route to confirmation
        try {
          const redirectTo = stripeCheckoutRedirects.success_url
          // Navigate internally to the relative route
          if (redirectTo) history.push(redirectTo)

          // Next, we reload the user's subscription, the expectation being
          // that this will trigger the next phase in whatever flow the SubscriptionPlanOrderForm
          // is a part of.
          // Reload legacy subscription store's user subscription.
          window.App?.getStore("subscribe").resource.subscription.load(
            {},
            true /* forceReload */
          )
          // Reload user's subscription in Apollo cache.
          userSubscriptionQuery.refetch()
        } catch {
          // We'll just have to simply reload the current URL, but it's unlikely
          // we'll make it here.
          window.location.reload()
        }
      }
    })

  // NOTE: visibilityCodeFilter only supports eq right now.
  const planVisibilityCode =
    visibilityCode || (visibilityCodeFilter && visibilityCodeFilter.eq)

  const isLifetimeSubscription =
    subscriptionPlan?.subscription_plan.interval === 20
  const subscriptionOrderData = {
    plan_id: subscriptionPlan.id,
    plan_visibility_code: planVisibilityCode,
    coupon_code: discount && discountCode ? discountCode : null,
    ...(isLifetimeSubscription ? {} : { redirect_to: redirectTo })
  }

  return (
    <>
      <SubscriptionPlanCard
        plan={subscriptionPlan}
        showPlanDetails={showPlanDetails}
      />
      <br />
      <ConditionalOrderDiscountForm
        schoolSubscriptionPlanId={subscriptionPlan.id!}
        applyDiscountCode={async (code) => {
          const discountQuery = await loadDiscount({
            variables: {
              discount_code: code,
              school_plan_id: subscriptionPlan.id!
            },
            fetchPolicy: "network-only"
          })
          const discount =
            discountQuery.data?.schoolSubscriptionDiscount || null
          setDiscount(discount)
          setDiscountCode(discount ? code : "")
          // TODO: need to fix typing here.
          return discount! as any
        }}
        clearDiscountCode={() => {
          setDiscount(null)
          setDiscountCode("")
        }}
      />
      <br />
      {shouldRedeemAsGiftSubscription ? (
        <CenteredContent>
          <SubmitButton
            styleType="primary"
            size="large"
            onClick={() => redeemSchoolGiftSubscription()}
          >
            {tc("Redeem")}
          </SubmitButton>
        </CenteredContent>
      ) : (
        <OrderStripeCheckoutForm
          orderType={
            isLifetimeSubscription
              ? OrderType.LifetimeSchoolSubscription
              : OrderType.SchoolSubscription
          }
          orderData={subscriptionOrderData}
          stripeCheckoutRedirects={stripeCheckoutRedirects}
        />
      )}
      {!!error && <Alert error={error} />}
    </>
  )
}

type SubscriptionPlanCardProps = {
  plan: SchoolSubscriptionPlanFragment
  showPlanDetails?: boolean
  onSelectPlan?: (plan: SchoolSubscriptionPlanFragment) => void
}

function SubscriptionPlanCard({
  plan,
  showPlanDetails,
  onSelectPlan
}: SubscriptionPlanCardProps) {
  const { t, tca } = useTranslate()
  const { currencyFormatter } = useCurrencyFormatter()

  return (
    <PWAdminHint model={plan.subscription_plan!}>
      <PlanCard key={plan.id}>
        <PlanHeader $hasImage={!!plan.image}>
          {!!plan.image && (
            <Image
              src={plan.image!}
              alt={plan.subscription_plan!.name || "Subscription Plan Image"}
            />
          )}
          <Text.H4>{plan.subscription_plan!.name}</Text.H4>
        </PlanHeader>
        {plan.details_html && showPlanDetails ? (
          <StyledHTMLBody
            dangerouslySetInnerHTML={{ __html: plan.details_html }}
          />
        ) : (
          <br />
        )}
        <Text.Body>
          <b style={{ color: "rgb(42, 46, 51)" }}>
            {currencyFormatter.format(plan.subscription_plan!.amount)}
          </b>{" "}
          {t("per")}{" "}
          {getIntervalDisplayName(plan.subscription_plan!.interval, t)}
        </Text.Body>
        {onSelectPlan ? (
          <>
            <br />
            <Button
              variant="solid"
              colorPalette="brand"
              onClick={() => onSelectPlan(plan)}
            >
              {tca("Subscribe")}
            </Button>
          </>
        ) : null}
      </PlanCard>
    </PWAdminHint>
  )
}

type PositionedSubscriptionPlanCardProps = {
  plan: SchoolSubscriptionPlanFragment
  showPlanDetails?: boolean
  onSelectPlan?: (plan: SchoolSubscriptionPlanFragment) => void
  index: number
}

function PositionedSubscriptionPlanCard({
  plan,
  showPlanDetails,
  onSelectPlan,
  index
}: PositionedSubscriptionPlanCardProps) {
  const { me } = usePathwrightContext()
  const { updatePositionedObject } = usePositioningContext()

  // Only allow superusers initially.
  const canPosition = !!me?.permissions?.has_pathwright_staff_access && !!index

  function handleMoveUp() {
    updatePositionedObject({
      item: plan,
      index: index - 1
    })
  }

  return (
    <Box pos="relative">
      <SubscriptionPlanCard
        plan={plan}
        showPlanDetails={showPlanDetails}
        onSelectPlan={onSelectPlan}
      />
      {/* Only allow superusers initially. */}
      {canPosition && (
        <Tooltip fitContent title="Move up (only visible to super users).">
          <IconButton
            className="SchoolSubscriptionPlanReorder"
            aria-label="reorder"
            children={<Pathicon icon="chevron-up" />}
            variant="subtle"
            size="xs"
            borderRadius="full"
            onClick={handleMoveUp}
            pos="absolute"
            top={0}
            right={0}
            mt={2}
            mr={2}
          />
        </Tooltip>
      )}
    </Box>
  )
}

const StyledHTMLBody = styled(Text.Body)`
  ul {
    list-style: unset;
    padding-left: 40px;
  }
`

const NarrowCard = styled(Card)`
  & > .View {
    max-width: 500px;
  }
  margin-bottom: 100px;

  /* Override background color for auth container */
  &:has(.AdaptedAuthContainer) .CardBody {
    background: white;
  }
`

const PlanCard = styled.div`
  border: 1px solid #ccc;
  border-radius: 10px;
  padding: 20px;
  position: relative;
  background-color: white;

  &:first-child {
    margin-top: 0;
    margin-bottom: 20px;

    .SchoolSubscriptionPlanReorder {
      display: none;
    }
  }

  &:not(:first-child) {
    margin: 10px 0 20px;
  }
`

const PlanHeader = styled.div<{ $hasImage: boolean }>`
  display: grid;
  grid-template-columns: ${({ $hasImage }) =>
    $hasImage ? "min-content 1fr" : "1fr"};
  grid-column-gap: 0.5em;
  align-items: center;
  img {
    width: 36px;
    height: 36px;
    min-width: 36px;
    min-height: 36px;
  }
`

const CenteredContent = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  text-align: center;
`

const StyledBlankSlate = styled(BlankSlate)`
  padding: 30px 40px 40px !important;
`
